import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Music, MessageCircle } from 'lucide-react';
import { StorefrontProvider, useStorefront } from './useStorefront';
import { StorefrontAuthProvider } from './useStorefrontAuth';
import DetectivePikachuSearch from './DetectivePikachuSearch';
import CartButton from './CartButton';
import MiniCart from './MiniCart';
import LockMenu from './LockMenu';
import MegaMenuCatalog from './MegaMenuCatalog';
import MobileCatalogAccordion from './MobileCatalogAccordion';
import { storeUrl } from './storeBase';
import './storefront.css';

const NAV_LINKS = [
  { to: storeUrl('/'), label: 'Home' },
  { to: storeUrl('/catalog'), label: 'Catalog' },
  { to: storeUrl('/events'), label: 'Events' },
  { to: storeUrl('/sell'), label: 'Sell to Us' },
  { to: storeUrl('/contact'), label: 'Contact' },
];

// Footer keeps the full set (About + Shipping live here, not in the header).
const FOOTER_LINKS = [
  { to: storeUrl('/'), label: 'Home' },
  { to: storeUrl('/catalog'), label: 'Catalog' },
  { to: storeUrl('/events'), label: 'Events' },
  { to: storeUrl('/sell'), label: 'Sell to Us' },
  { to: storeUrl('/about'), label: 'About' },
  { to: storeUrl('/shipping'), label: 'Shipping' },
  { to: storeUrl('/contact'), label: 'Contact' },
];

const Wordmark = ({ logoUrl }) => (
  logoUrl
    ? <img
        src={logoUrl}
        alt="RAD GameShop"
        className="h-11 sm:h-[52px] w-auto object-contain"
        data-testid="sf-logo"
      />
    : <span className="sf-wordmark" data-testid="sf-wordmark">RAD</span>
);

const StorefrontShell = () => {
  const { settings, loading, error } = useStorefront();
  const location = useLocation();
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close the mobile menu whenever the route changes
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // SEO meta + favicon — light-touch since we don't ship react-helmet.
  // We inject a `<link rel="icon">` whenever the uploaded logo URL changes
  // so the browser tab + bookmarks reflect the storefront brand. Restore
  // the original favicon on unmount so leaving /store doesn't pollute the
  // staff app's tab icon.
  useEffect(() => {
    if (settings?.seo_title) document.title = settings.seo_title;
  }, [settings?.seo_title]);

  useEffect(() => {
    // Favicon — points to the cropped RAD-only square PNG so the tab icon
    // stays legible at 16/32 px. The full storefront logo upload sits in
    // /public/rad-logo.png; the favicon variant in /public/rad-favicon.png.
    const faviconHref = '/rad-favicon.png';
    const existing = Array.from(document.querySelectorAll('link[rel~="icon"]'));
    const prev = existing.map((l) => ({ el: l, href: l.getAttribute('href'), type: l.getAttribute('type') }));
    existing.forEach((l) => l.remove());
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = faviconHref;
    link.setAttribute('data-storefront-favicon', 'true');
    document.head.appendChild(link);
    return () => {
      link.remove();
      prev.forEach(({ el, href, type }) => {
        const restored = el.cloneNode();
        if (href) restored.setAttribute('href', href);
        if (type) restored.setAttribute('type', type);
        document.head.appendChild(restored);
      });
    };
  }, []);

  if (loading) {
    return (
      <div className="storefront-root flex items-center justify-center min-h-screen">
        <div className="sf-label animate-pulse">Loading…</div>
      </div>
    );
  }

  // Storefront not live — friendly splash, not a 404
  if (error === 'not-live') {
    return (
      <div className="storefront-root flex flex-col items-center justify-center min-h-screen text-center px-6">
        <span className="sf-wordmark text-5xl">RAD</span>
        <p className="sf-label mt-8">Coming soon</p>
        <p className="mt-3 max-w-md text-zinc-400">
          The shop is closed for restocking. Check back in a few — and follow us on Instagram for drop alerts.
        </p>
      </div>
    );
  }

  if (error === 'fetch-error' || !settings) {
    return (
      <div className="storefront-root flex flex-col items-center justify-center min-h-screen text-center px-6">
        <span className="sf-wordmark text-5xl">RAD</span>
        <p className="sf-label mt-8 text-red-400">Something broke</p>
        <p className="mt-3 text-zinc-400">We couldn&apos;t load the shop. Try refreshing in a moment.</p>
      </div>
    );
  }

  const socials = [
    ['social_instagram', Instagram, 'Instagram'],
    ['social_tiktok', Music, 'TikTok'],
    ['social_facebook', Facebook, 'Facebook'],
    ['social_twitter', Twitter, 'Twitter'],
    ['social_discord', MessageCircle, 'Discord'],
  ].filter(([key]) => settings[key]);

  return (
    <div className="storefront-root">
      {/* Glass nav */}
      <header className="sf-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {/* Mobile menu trigger — D-pad icon, sits left of the logo */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              className="md:hidden inline-flex items-center justify-center hover:-translate-y-0.5 active:translate-y-0.5 transition-transform"
              data-testid="sf-nav-mobile-menu"
            >
              <LockMenu size={32} open={mobileMenuOpen} />
            </button>
            <Link to={storeUrl('/')} className="flex items-center" data-testid="sf-nav-home">
              <Wordmark logoUrl={settings.logo_url} />
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-8" data-testid="sf-nav-links">
            {NAV_LINKS.map((l) => {
              const active = location.pathname === l.to;
              if (l.to === storeUrl('/catalog')) {
                return <MegaMenuCatalog key={l.to} active={active} />;
              }
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`sf-label text-xs ${active ? 'text-white' : 'hover:text-white text-zinc-400 transition-colors'}`}
                  data-testid={`sf-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              );
            })}
            {/* SEGA-styled My Account chip — sits after Contact, stands out */}
            <Link
              to={storeUrl('/account')}
              className="sf-nav-account-chip"
              data-testid="sf-nav-account"
              aria-label="My account"
            >
              <span className="sf-nav-account-chip-text">MY&nbsp;ACCOUNT</span>
            </Link>
          </nav>
          <div className="flex items-center gap-1 md:gap-3">
            <Link
              to={storeUrl('/catalog')}
              aria-label="Search catalog"
              data-testid="sf-nav-search"
              title="Search the catalog"
              className="inline-flex items-center justify-center hover:-translate-y-0.5 active:translate-y-0.5 transition-transform"
            >
              <DetectivePikachuSearch size={32} />
            </Link>
            <CartButton onOpen={() => setCartOpen(true)} />
          </div>
        </div>

        {/* Mobile slide-down panel */}
        <div
          className={`sf-mobile-menu md:hidden ${mobileMenuOpen ? 'open' : ''}`}
          data-testid="sf-mobile-menu-panel"
        >
          <nav className="flex flex-col">
            {NAV_LINKS.map((l) => {
              const active = location.pathname === l.to;
              if (l.to === storeUrl('/catalog')) {
                return <MobileCatalogAccordion key={l.to} active={active} />;
              }
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`sf-mobile-menu-item ${active ? 'active' : ''}`}
                  data-testid={`sf-nav-mobile-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              );
            })}
            <Link
              to={storeUrl('/account')}
              className="sf-mobile-menu-item sf-mobile-menu-item-account"
              data-testid="sf-nav-mobile-account"
            >My Account</Link>
          </nav>
        </div>
      </header>

      <MiniCart open={cartOpen} onClose={() => setCartOpen(false)} />

      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 grid gap-10 md:grid-cols-4">
          <div>
            <Wordmark logoUrl={settings.logo_url} />
            <p className="mt-4 text-zinc-400 text-sm max-w-xs">{settings.about_text?.slice(0, 140) || 'Pokémon, MTG, video games and more.'}</p>
          </div>
          <div>
            <p className="sf-label mb-3">Shop</p>
            <ul className="space-y-2 text-zinc-300">
              {FOOTER_LINKS.map((l) => (
                <li key={l.to}><Link to={l.to} className="hover:text-white transition-colors text-sm">{l.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="sf-label mb-3">Contact</p>
            <ul className="space-y-2 text-zinc-300 text-sm">
              {settings.contact_email && <li><a href={`mailto:${settings.contact_email}`} className="hover:text-white">{settings.contact_email}</a></li>}
              {settings.contact_phone && <li>{settings.contact_phone}</li>}
              {settings.contact_address && <li className="whitespace-pre-line">{settings.contact_address}</li>}
            </ul>
          </div>
          <div>
            <p className="sf-label mb-3">Follow</p>
            <div className="flex gap-3">
              {socials.length === 0 && <span className="text-zinc-500 text-sm">—</span>}
              {socials.map(([key, Icon, name]) => (
                <a
                  key={key}
                  href={settings[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-white/10 hover:border-[#9B5CF6] hover:text-[#9B5CF6] flex items-center justify-center transition-colors"
                  aria-label={name}
                  data-testid={`sf-social-${name.toLowerCase()}`}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-white/5 py-5 text-center text-xs text-zinc-500 tracking-widest uppercase">
          © {new Date().getFullYear()} — Powered by Shopsoft
        </div>
      </footer>
    </div>
  );
};

const StorefrontLayout = () => (
  <StorefrontAuthProvider>
    <StorefrontProvider>
      <StorefrontShell />
    </StorefrontProvider>
  </StorefrontAuthProvider>
);

export default StorefrontLayout;
